/**
 * K8sTerminal — Terminal web profissional via kubectl exec (xterm.js + WebSocket)
 *
 * Estilo Google Cloud Shell / Render / Railway.
 * xterm.js carregado via CDN (sem npm install).
 * Preact — hooks from 'preact/hooks'.
 */
import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { api } from '../api/client';
import { T } from '../styles/tokens';
import { SkeletonBar, Button } from '../components/ui';

// ── CDN URLs ──
const XTERM_JS = 'https://cdn.jsdelivr.net/npm/xterm@5.3.0/lib/xterm.min.js';
const XTERM_CSS = 'https://cdn.jsdelivr.net/npm/xterm@5.3.0/css/xterm.min.css';
const XTERM_FIT_JS = 'https://cdn.jsdelivr.net/npm/@xterm/addon-fit@0.10.0/lib/addon-fit.min.js';

// ── Types ──
type ConnectionStatus = 'idle' | 'loading' | 'connected' | 'reconnecting' | 'error';

interface TerminalTokenResponse {
  data: {
    wsUrl: string;
    expiresAt: number;
  };
}

// ── CDN Loader ──
function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement('script');
    s.src = src;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
}

function loadCSS(href: string): void {
  if (document.querySelector(`link[href="${href}"]`)) return;
  const l = document.createElement('link');
  l.rel = 'stylesheet';
  l.href = href;
  document.head.appendChild(l);
}

// ── Styles ──
const COLORS = {
  bg: T.bg,
  bgHeader: T.bgInput,
  bgDarker: '#010409',
  border: T.border,
  text: T.text,
  textMuted: '#7d8590',
  green: T.success,
  yellow: T.warning,
  red: T.danger,
  blue: T.primary,
};

// ── Component ──
export function K8sTerminal({ workspaceId, workspaceName, onClose }: {
  workspaceId: string;
  workspaceName?: string;
  onClose?: () => void;
}) {
  const [status, setStatus] = useState<ConnectionStatus>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [xtermReady, setXtermReady] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const termRef = useRef<any>(null);
  const fitRef = useRef<any>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attemptRef = useRef(0);
  const aliveRef = useRef(true);

  // Load xterm.js from CDN
  useEffect(() => {
    loadCSS(XTERM_CSS);
    loadScript(XTERM_JS)
      .then(() => loadScript(XTERM_FIT_JS))
      .then(() => setXtermReady(true))
      .catch((e) => {
        setStatus('error');
        setErrorMsg(`Erro ao carregar terminal: ${e.message}`);
      });

    return () => { aliveRef.current = false; };
  }, []);

  // Initialize xterm once loaded
  useEffect(() => {
    if (!xtermReady || !containerRef.current) return;

    const Terminal = (window as any).Terminal;
    const FitAddon = (window as any).FitAddon?.FitAddon;

    if (!Terminal) {
      setStatus('error');
      setErrorMsg('xterm.js não carregou corretamente.');
      return;
    }

    const term = new Terminal({
      cursorBlink: true,
      cursorStyle: 'bar',
      fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', Consolas, monospace",
      fontSize: 14,
      lineHeight: 1.2,
      theme: {
        background: COLORS.bg,
        foreground: COLORS.text,
        cursor: COLORS.green,
        cursorAccent: COLORS.bg,
        selectionBackground: '#264f78',
        selectionForeground: T.text,
        black: T.bg,
        red: T.danger,
        green: T.success,
        yellow: T.warning,
        blue: T.primary,
        magenta: '#bc8cff',
        cyan: '#76e3ea',
        white: T.text,
        brightBlack: '#484f58',
        brightRed: '#ff7b72',
        brightGreen: '#56d364',
        brightYellow: '#e3b341',
        brightBlue: T.primary,
        brightMagenta: '#d2a8ff',
        brightCyan: T.primary,
        brightWhite: '#f0f6fc',
      },
      scrollback: 5000,
      allowProposedApi: true,
    });

    let fit: any = null;
    if (FitAddon) {
      fit = new FitAddon();
      term.loadAddon(fit);
      fitRef.current = fit;
    }

    term.open(containerRef.current);
    if (fit) {
      try { fit.fit(); } catch {}
    }
    termRef.current = term;

    // Auto-resize
    const handleResize = () => {
      if (fitRef.current) {
        try { fitRef.current.fit(); } catch {}
      }
      // Send resize to WS
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'resize', cols: term.cols, rows: term.rows }));
      }
    };
    const ro = new ResizeObserver(handleResize);
    ro.observe(containerRef.current);
    window.addEventListener('resize', handleResize);

    // Connect
    connect(term);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', handleResize);
      term.dispose();
      termRef.current = null;
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
    };
  }, [xtermReady]);

  const connect = useCallback(async (term?: any) => {
    const t = term || termRef.current;
    if (!t || !aliveRef.current) return;

    setStatus('loading');
    setErrorMsg('');

    try {
      const res = await api.get<TerminalTokenResponse>(`/cloud/workspaces/${workspaceId}/terminal-token`);
      const { wsUrl } = (res as any)?.data || res;

      if (!wsUrl) throw new Error('Token não recebido');

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!aliveRef.current) return;
        setStatus('connected');
        attemptRef.current = 0;
        // Send initial resize
        if (t.cols && t.rows) {
          ws.send(JSON.stringify({ type: 'resize', cols: t.cols, rows: t.rows }));
        }
      };

      ws.onmessage = (e) => {
        if (!aliveRef.current) return;
        const data = e.data;
        // Check if it's a JSON control message
        try {
          const parsed = JSON.parse(data);
          if (parsed.type === 'error') {
            t.writeln(`\r\n\x1b[31m${parsed.message}\x1b[0m`);
            return;
          }
          if (parsed.type === 'exit') {
            t.writeln(`\r\n\x1b[33mSessão encerrada (código: ${parsed.code})\x1b[0m`);
            setStatus('error');
            setErrorMsg('Sessão encerrada');
            return;
          }
        } catch {
          // Not JSON — regular terminal output
        }
        t.write(data);
      };

      ws.onclose = () => {
        if (!aliveRef.current) return;
        setStatus('reconnecting');
        scheduleReconnect();
      };

      ws.onerror = () => {
        if (!aliveRef.current) return;
        setStatus('error');
        setErrorMsg('Erro de conexão WebSocket');
      };

      // Forward terminal input to WS
      t.onData((data: string) => {
        if (ws.readyState === WebSocket.OPEN) ws.send(data);
      });

    } catch (e: any) {
      const msg = e?.message || 'Erro desconhecido';
      setStatus('error');
      setErrorMsg(msg);
      t.writeln(`\r\n\x1b[31m${msg}\x1b[0m`);
      scheduleReconnect();
    }
  }, [workspaceId]);

  const scheduleReconnect = useCallback(() => {
    if (reconnectRef.current) clearTimeout(reconnectRef.current);
    attemptRef.current++;
    const delay = Math.min(1000 * Math.pow(2, attemptRef.current - 1), 30000);
    setStatus('reconnecting');
    reconnectRef.current = setTimeout(() => {
      if (aliveRef.current) connect();
    }, delay);
  }, [connect]);

  const handleReconnect = useCallback(() => {
    if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    if (reconnectRef.current) clearTimeout(reconnectRef.current);
    attemptRef.current = 0;
    connect();
  }, [connect]);

  const statusDot = status === 'connected' ? COLORS.green
    : status === 'reconnecting' || status === 'loading' ? COLORS.yellow
    : status === 'error' ? COLORS.red
    : COLORS.textMuted;

  const statusText = status === 'connected' ? 'Conectado'
    : status === 'loading' ? 'Conectando...'
    : status === 'reconnecting' ? 'Reconectando...'
    : status === 'error' ? 'Desconectado'
    : 'Aguardando';

  const wrapperStyle: any = {
    display: 'flex',
    flexDirection: 'column',
    background: COLORS.bg,
    borderRadius: isFullscreen ? 0 : 12,
    border: isFullscreen ? 'none' : `1px solid ${COLORS.border}`,
    overflow: 'hidden',
    ...(isFullscreen ? {
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      height: '100vh',
      width: '100vw',
    } : {
      height: '100%',
      minHeight: 400,
    }),
  };

  return (
    <div style={wrapperStyle}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 16px',
        background: COLORS.bgHeader,
        borderBottom: `1px solid ${COLORS.border}`,
        flexShrink: 0,
        userSelect: 'none',
      }}>
        {/* Status dot */}
        <div style={{
          width: 10, height: 10, borderRadius: '50%',
          background: statusDot,
          boxShadow: `0 0 6px ${statusDot}60`,
          flexShrink: 0,
        }} />

        {/* Title */}
        <span style={{
          flex: 1, fontSize: 13, color: COLORS.text,
          fontFamily: "'JetBrains Mono', monospace",
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {workspaceName || workspaceId}
          <span style={{ color: COLORS.textMuted, marginLeft: 8, fontSize: 11 }}>
            — {statusText}
          </span>
        </span>

        {/* Buttons */}
        {status === 'error' && (
          <Button variant="ghost"
            onClick={handleReconnect}
            style={{
              background: COLORS.green, border: 'none', borderRadius: 6,
              color: T.text, cursor: 'pointer', fontSize: 11,
              padding: '4px 10px', fontWeight: 600,
            }}
          >Reconectar</Button>
        )}

        <Button variant="ghost"
          onClick={() => setIsFullscreen(f => !f)}
          style={{
            background: 'none', border: `1px solid ${COLORS.border}`,
            borderRadius: 6, color: COLORS.textMuted, cursor: 'pointer',
            fontSize: 13, padding: '3px 8px',
          }}
          title={isFullscreen ? 'Sair do fullscreen' : 'Tela cheia'}
        >{isFullscreen ? '⊡' : '⤢'}</Button>

        {onClose && (
          <Button variant="ghost"
            onClick={onClose}
            style={{
              background: 'none', border: `1px solid ${COLORS.border}`,
              borderRadius: 6, color: COLORS.textMuted, cursor: 'pointer',
              fontSize: 13, padding: '3px 8px',
            }}
            title="Fechar terminal"
          ></Button>
        )}
      </div>

      {/* Terminal container */}
      {!xtermReady ? (
        <div style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: 12, color: COLORS.textMuted,
        }}>
          <div style={{
            width: 24, height: 24, border: `3px solid ${COLORS.border}`,
            borderTop: `3px solid ${COLORS.green}`, borderRadius: '50%',
            animation: 'k8s-spin 0.8s linear infinite',
          }} />
          <span style={{ display: 'inline-block' }}><SkeletonBar width={120} height={13} /></span>
          <style>{`@keyframes k8s-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : (
        <div
          ref={containerRef}
          style={{ flex: 1, minHeight: 0, padding: '4px 0 0 4px' }}
        />
      )}
    </div>
  );
}

// ── Route page wrapper ──
export function K8sTerminalPage({ id }: { id: string; path?: string }) {
  const [workspaceName, setWorkspaceName] = useState('');

  useEffect(() => {
    // Try to get workspace name from DB
    api.get<any>(`/cloud/workspaces/${id}/status`).then((res: any) => {
      const data = res?.data || res;
      if (data?.name) setWorkspaceName(data.name);
    }).catch(() => {});
  }, [id]);

  const handleClose = useCallback(() => {
    window.history.back();
  }, []);

  return (
    <div style={{ height: 'calc(100vh - 64px)', padding: 16 }}>
      <K8sTerminal
        workspaceId={id}
        workspaceName={workspaceName || `Ambiente ${id}`}
        onClose={handleClose}
      />
    </div>
  );
}
